// Globals
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { bulkUpdateRegionsAndPrices, getProductRegionsAndPrices, productsSelector } from '../../feature';

// Models
import { IBulkUpdateStockItem } from '@/models/products.model';
import { ROUTES } from '@/models/router.model';

// Components
import {
  StyledHeaderBlock,
  StyledContainerTitle,
  StyledContainerWrapper,
  StyledContentBlock,
  StyledAddMoreBlock,
  StyledAddMoreText,
  StyledAddMoreLink,
  StyledModalButtonsWrapper,
  StyledModalTitle,
} from './ProductDetailsRegionsAndPricesContainerStyles';
import { ProductRegionsAndPricesList } from '../../components';
import { Button } from '@/ui';
import { Modal, ModalSize, showErrorToast, showSuccessToast } from '@/components';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { useBoolean, useModal } from '@/hooks';

const ProductDetailsRegionsAndPricesContainer = () => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { value: isEditing, setFalse: disableEditing, setTrue: enableEditing } = useBoolean();
  const [editedData, setEditedData] = useState<Map<string, IBulkUpdateStockItem>>(new Map());
  const { isOpened, openModal, closeModal } = useModal();

  const [allRegionsPrice, setAllRegionsPrice] = useState<number | null>(null);
  const [allRegionsDiscountPrice, setAllRegionsDiscountPrice] = useState<number | null>(null);

  const { productRegionsAndPrices, isLoading } = useAppState(productsSelector);
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);

  const handleFieldChange = useCallback(
    (rowId: string, field: keyof IBulkUpdateStockItem, value: boolean | number) => {
      if (field === 'price') setAllRegionsPrice(null);
      if (field === 'discount_price') setAllRegionsDiscountPrice(null);
      setEditedData((prev) => {
        const newMap = new Map(prev);
        const currentRow = productRegionsAndPrices?.find((item) => item.id === rowId);

        if (!currentRow) return prev;

        const existingEdit = newMap.get(rowId) || {
          id: rowId,
          available: currentRow.available,
          price: Number(currentRow.price),
          discount_price: currentRow.discount_price ? Number(currentRow.discount_price) : undefined,
        };

        newMap.set(rowId, { ...existingEdit, [field]: value });
        return newMap;
      });
    },
    [productRegionsAndPrices],
  );

  const handleAllRegionsFieldChange = useCallback(
    (field: 'price' | 'discount_price', value: number | null) => {
      if (field === 'price') setAllRegionsPrice(value);
      else setAllRegionsDiscountPrice(value);

      if (!productRegionsAndPrices) return;

      setEditedData((prev) => {
        const newMap = new Map(prev);
        productRegionsAndPrices.forEach((item) => {
          const existing = newMap.get(item.id) || {
            id: item.id,
            available: item.available,
            price: Number(item.price),
            discount_price: item.discount_price ? Number(item.discount_price) : undefined,
          };
          newMap.set(item.id, { ...existing, [field]: value ?? undefined });
        });
        return newMap;
      });
    },
    [productRegionsAndPrices],
  );

  const handleCancel = useCallback(() => {
    setEditedData(new Map());
    setAllRegionsPrice(null);
    setAllRegionsDiscountPrice(null);
    disableEditing();
  }, [disableEditing]);

  const checkIfPriceChanged = useCallback(() => {
    const entries = Array.from(editedData.entries());
    for (let i = 0; i < entries.length; i++) {
      const [rowId, editedRow] = entries[i];
      const originalRow = productRegionsAndPrices?.find((item) => item.id === rowId);
      if (!originalRow) continue;

      const originalPrice = Number(originalRow.price);
      const originalDiscountPrice = originalRow.discount_price ? Number(originalRow.discount_price) : undefined;

      if (editedRow.price !== originalPrice || editedRow.discount_price !== originalDiscountPrice) {
        return true;
      }
    }
    return false;
  }, [editedData, productRegionsAndPrices]);

  const performSave = useCallback(async () => {
    try {
      const stocks = Array.from(editedData.values());
      await dispatch(bulkUpdateRegionsAndPrices({ stocks })).unwrap();
      showSuccessToast({ message: t('data_saved') });
      setEditedData(new Map());
      setAllRegionsPrice(null);
      setAllRegionsDiscountPrice(null);
      disableEditing();
      closeModal();
      // Reload data after successful save
      if (id) {
        dispatch(getProductRegionsAndPrices(id));
      }
    } catch {
      showErrorToast({ message: t('product_update_validation_error') });
    }
  }, [editedData, dispatch, disableEditing, closeModal, t, id]);

  const handleSave = useCallback(async () => {
    if (editedData.size === 0) {
      disableEditing();
      return;
    }

    if (checkIfPriceChanged()) {
      openModal();
    } else {
      await performSave();
    }
  }, [editedData, disableEditing, checkIfPriceChanged, openModal, performSave]);

  useEffect(() => {
    if (id) {
      dispatch(getProductRegionsAndPrices(id));
    }
  }, [id, dispatch]);

  return (
    <StyledContainerWrapper>
      <StyledHeaderBlock>
        <StyledContainerTitle>{t('regions_and_prices_tab_title')}</StyledContainerTitle>
        {canManageProduct &&
          (isEditing ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={handleCancel} type="text">
                {t('products_list_cancel')}
              </Button>
              <Button onClick={handleSave} type="primary" loading={isLoading}>
                {t('regions_and_prices_save_button')}
              </Button>
            </div>
          ) : (
            <Button onClick={enableEditing} type="primary">
              {t('regions_and_prices_edit_button')}
            </Button>
          ))}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <ProductRegionsAndPricesList
          productsRegionsAndPrices={productRegionsAndPrices}
          isLoading={isLoading}
          isEditing={isEditing}
          editedData={editedData}
          onFieldChange={handleFieldChange}
          allRegionsPrice={allRegionsPrice}
          allRegionsDiscountPrice={allRegionsDiscountPrice}
          onAllRegionsFieldChange={handleAllRegionsFieldChange}
        />
        <StyledAddMoreBlock>
          <StyledAddMoreText>{t('regions_and_prices_add_more_text')}</StyledAddMoreText>
          <StyledAddMoreLink href={`${ROUTES.REGIONS_AND_PICKUP_INDEX}`} target="_blank" rel="noreferrer">
            {t('regions_and_prices_add_more_link_text')}
          </StyledAddMoreLink>
        </StyledAddMoreBlock>
      </StyledContentBlock>
      <Modal visible={isOpened} onCancel={closeModal} size={ModalSize.LARGE}>
        <StyledModalTitle>{t('products_price_change_modal_title')}</StyledModalTitle>
        <StyledModalTitle>{t('products_price_change_modal_description')}</StyledModalTitle>
        <StyledModalButtonsWrapper>
          <Button onClick={closeModal} loading={isLoading} type="text">
            {t('products_import_back')}
          </Button>
          <Button onClick={performSave} loading={isLoading} type="primary">
            {t('regions_and_prices_save_button')}
          </Button>
        </StyledModalButtonsWrapper>
      </Modal>
    </StyledContainerWrapper>
  );
};

export default ProductDetailsRegionsAndPricesContainer;
